import { useFragment, useMutation } from '@provision/graphql-client';
import { useCallback, useMemo } from 'react';
import { writeThreadInCache } from 'src/graphql/entities/conversation-message-reply/conversation-message-reply.cache';
import {
  MESSAGE_THREAD_FRAGMENT,
  RESOLVE_CONVERSATION_MESSAGE_THREAD_MUTATION,
  UNRESOLVE_CONVERSATION_MESSAGE_THREAD_MUTATION,
} from 'src/graphql/entities/conversation-message-reply/conversation-message-reply.operations';
import {
  MessageReply,
  MessageThreadData,
  ResolveConversationMessageThreadResult,
  UnresolveConversationMessageThreadResult,
} from 'src/graphql/entities/conversation-message-reply/conversation-message-reply.types';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { useSnackbar } from 'src/utils/hooks/use-snackbar';

const NO_REPLIES: MessageReply[] = [];

export interface MessageThread {
  /** Live replies, oldest first. */
  replies: MessageReply[];
  /** The reply that resolved the thread, if it is resolved. */
  resolutionId: string | null;
  resolveThread: (replyId: string) => void;
  unresolveThread: () => void;
}

/**
 * The replies of a message and the state of its thread, read from its cached
 * `ConversationMessage`, with optimistic resolve and unresolve.
 */
export function useMessageThread(conversationMessageId: string): MessageThread {
  const { user } = useCurrentUserProfile();
  const { showErrorAlert } = useSnackbar();

  const fragment = useFragment<MessageThreadData>({
    fragment: MESSAGE_THREAD_FRAGMENT,
    fragmentName: 'MessageThread',
    from: { __typename: 'ConversationMessage', id: conversationMessageId },
  });
  // A message cached without its thread (e.g. not from the timeline query) shows none.
  const data = fragment.complete ? fragment.data : undefined;

  const records = data?.replies;
  const replies = useMemo(
    () =>
      records
        ? records
            .filter((reply) => !reply.deletedAt)
            .toSorted((a, b) => a.createdAt.localeCompare(b.createdAt))
        : NO_REPLIES,
    [records],
  );

  const thread = data?.thread ?? null;
  const resolutionId = thread?.resolutionConversationMessageId ?? null;

  const [resolveMutation] = useMutation<ResolveConversationMessageThreadResult>(
    RESOLVE_CONVERSATION_MESSAGE_THREAD_MUTATION,
    {
      update: (cache, { data: result }) => {
        if (result) {
          writeThreadInCache(
            cache,
            conversationMessageId,
            result.resolveConversationMessageThread,
          );
        }
      },
    },
  );

  const [unresolveMutation] = useMutation<UnresolveConversationMessageThreadResult>(
    UNRESOLVE_CONVERSATION_MESSAGE_THREAD_MUTATION,
    {
      update: (cache, { data: result }) => {
        if (result) {
          writeThreadInCache(
            cache,
            conversationMessageId,
            result.unresolveConversationMessageThread,
          );
        }
      },
    },
  );

  const userId = user?.userId;

  const resolveThread = useCallback(
    (replyId: string) => {
      const reply = replies.find((it) => it.id === replyId);
      if (!reply || !userId) {
        return;
      }
      const threadId = thread?.id ?? `optimistic:${conversationMessageId}`;
      resolveMutation({
        variables: { params: { replyConversationMessageId: replyId } },
        optimisticResponse: {
          resolveConversationMessageThread: {
            __typename: 'ConversationMessageThread',
            id: threadId,
            conversationMessageThreadId: threadId,
            conversationMessageId,
            conversationId: reply.conversationId,
            partnerId: reply.partnerId,
            resolutionConversationMessageId: replyId,
            resolvedByUserId: userId,
            resolvedAt: new Date().toISOString(),
          },
        },
      }).catch(() => showErrorAlert('Could not resolve the thread. Please try again.'));
    },
    [conversationMessageId, replies, resolveMutation, showErrorAlert, thread?.id, userId],
  );

  const unresolveThread = useCallback(() => {
    if (!thread) {
      return;
    }
    unresolveMutation({
      variables: { params: { conversationMessageId } },
      optimisticResponse: {
        unresolveConversationMessageThread: {
          ...thread,
          resolutionConversationMessageId: null,
          resolvedByUserId: null,
          resolvedAt: null,
        },
      },
    }).catch(() => showErrorAlert('Could not reopen the thread. Please try again.'));
  }, [conversationMessageId, showErrorAlert, thread, unresolveMutation]);

  return { replies, resolutionId, resolveThread, unresolveThread };
}
